import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Calendar, Clock, Loader2, Video, Building2, MailCheck } from 'lucide-react';
import { useCalendarEventsQuery } from '../hooks/useCalendarQueries';
import { CalendarEventDetailDialog } from './CalendarEventDetailDialog';
import { EVENT_STATUS_CONFIG, type CalendarEvent } from '../types/calendar';

interface CVMeetingsSectionProps {
  cvId: string;
  workspaceId: string | undefined;
  idToken: string | undefined;
}

export function CVMeetingsSection({ cvId, workspaceId, idToken }: CVMeetingsSectionProps) {
  const { t } = useTranslation('ats');
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, isLoading, error } = useCalendarEventsQuery({
    workspaceId,
    idToken,
    cvId,
    enabled: !!workspaceId && !!idToken && !!cvId,
  });

  const sortedEvents = useMemo(() => {
    const items = data?.data.items ?? [];
    return [...items].sort(
      (a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime()
    );
  }, [data]);

  const now = Date.now();

  const handleSelect = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Calendar className="h-5 w-5 text-blue-500" />
        {t('cv.detail.meetings')}
        {sortedEvents.length > 0 && (
          <span className="text-sm font-normal text-gray-500">({sortedEvents.length})</span>
        )}
      </h3>

      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">
          {error instanceof Error ? error.message : t('cv.detail.meetingsLoadFailed')}
        </p>
      ) : sortedEvents.length === 0 ? (
        <p className="text-sm text-gray-400">{t('cv.detail.noMeetings')}</p>
      ) : (
        <ul className="space-y-2">
          {sortedEvents.map((event) => {
            const start = parseISO(event.start_time);
            const end = parseISO(event.end_time);
            const isPast = start.getTime() < now;
            const statusConfig = EVENT_STATUS_CONFIG[event.status] ?? {
              label: event.status,
              color: 'bg-gray-100 text-gray-700',
            };

            return (
              <li key={event.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(event)}
                  className={`w-full text-left border rounded-lg p-3 transition-colors hover:border-blue-300 hover:bg-blue-50/30 ${
                    isPast ? 'border-gray-200 bg-gray-50/50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{event.title}</p>
                    </div>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${statusConfig.color}`}
                    >
                      {statusConfig.label}
                    </span>
                    {event.template_email_sent_at && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 flex-shrink-0">
                        <MailCheck className="h-3 w-3" />
                        {t('cv.detail.emailSentBadge')}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-1">
                    <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>
                      {format(start, 'dd/MM/yyyy')} · {format(start, 'HH:mm')}–{format(end, 'HH:mm')}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {event.location_type === 'online' ? (
                      <span className="inline-flex items-center gap-1">
                        <Video className="h-3 w-3" /> {t('cv.detail.online')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <Building2 className="h-3 w-3" /> {t('cv.detail.offline')}
                      </span>
                    )}
                    {event.job_code && <span className="truncate">{event.job_code}</span>}
                    {event.event_type_name && (
                      <span className="truncate">{event.event_type_name}</span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <CalendarEventDetailDialog
        event={selectedEvent}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        readOnly
      />
    </div>
  );
}
